import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { resolveLandingContent, type LandingPageDefinition } from "./registry";
import { parseLandingDocument } from "./resolve";
import type { LandingContent } from "./schema";
import { isLandingPreview, PREVIEW_READY, readPreviewMessage } from "./preview";

interface LandingContentValue {
  definition: LandingPageDefinition;
  content: LandingContent;
  preview: boolean;
}
const LandingContext = createContext<LandingContentValue | null>(null);
const fetched = new Map<string, unknown>();

async function fetchPublished(definition: LandingPageDefinition): Promise<unknown> {
  try {
    const query = Promise.resolve(
      supabase.from("site_content").select("content_value").eq("content_key", definition.contentKey).maybeSingle(),
    ).then(({ data }) => parseLandingDocument(data?.content_value), () => null);
    return await Promise.race([query, new Promise((resolve) => setTimeout(() => resolve(null), 4000))]);
  } catch {
    return null;
  }
}

/** Raw published content if it is already known, otherwise undefined. */
function knownRaw(definition: LandingPageDefinition): unknown {
  const early = typeof window !== "undefined" ? window.__landingContent : undefined;
  if (early && early.slug === definition.slug && early.status === "ready") return early.value ?? null;
  if (fetched.has(definition.slug)) return fetched.get(definition.slug);
  return undefined;
}

export function LandingContentProvider({
  definition,
  children,
}: {
  definition: LandingPageDefinition;
  children: ReactNode;
}) {
  const [preview] = useState(() => isLandingPreview());
  const [raw, setRaw] = useState<unknown>(() => knownRaw(definition));
  const [override, setOverride] = useState<LandingContent | null>(null);

  useEffect(() => {
    if (raw !== undefined) return;
    let active = true;
    const early = window.__landingContent;
    const source =
      early && early.slug === definition.slug
        ? Promise.resolve(early.promise).catch(() => null)
        : fetchPublished(definition).then((value) => {
            fetched.set(definition.slug, value);
            return value;
          });
    void source.then((value) => {
      if (active) setRaw(value ?? null);
    });
    return () => {
      active = false;
    };
  }, [definition, raw]);

  // Content that arrives after the wait limit replaces the originals when it lands.
  useEffect(() => {
    const early = window.__landingContent;
    if (!early || early.slug !== definition.slug) return;
    const onLate = () => setRaw(early.value ?? null);
    window.addEventListener("landing-content", onLate);
    return () => window.removeEventListener("landing-content", onLate);
  }, [definition]);

  useEffect(() => {
    if (!preview) return;
    const onMessage = (event: MessageEvent) => {
      const content = readPreviewMessage(event, definition.slug);
      if (content !== undefined) setOverride(resolveLandingContent(definition, content));
    };
    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type: PREVIEW_READY, slug: definition.slug }, window.location.origin);
    return () => window.removeEventListener("message", onMessage);
  }, [definition, preview]);

  if (!override && raw === undefined) return <div className="web-dev-landing" aria-busy="true" />;
  const content = override ?? resolveLandingContent(definition, raw);
  return <LandingContext.Provider value={{ definition, content, preview }}>{children}</LandingContext.Provider>;
}

export function useLandingContent(): LandingContentValue {
  const value = useContext(LandingContext);
  if (!value) throw new Error("useLandingContent must be used inside LandingContentProvider");
  return value;
}

/** Supplies already-resolved content, for rendering outside the page (shell generator, tests). */
export function StaticLandingContent({
  definition,
  content,
  preview = false,
  children,
}: Omit<LandingContentValue, "preview"> & { preview?: boolean; children: ReactNode }) {
  return <LandingContext.Provider value={{ definition, content, preview }}>{children}</LandingContext.Provider>;
}
